import { makeAutoObservable } from 'mobx';
import { RouteV2Id } from 'routes/routesConfigV2';
import GroupModule from 'store/modules/group/groupModule';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { redirectToRoute } from 'routes/redirectToRoute';
import CartModule from 'store/modules/cart/cartModule';
import { QuoteFlowSidebarDelegate } from './sidebarDelegate';
import { QuoteFlowGroupInfoDelegate } from './quoteFlowGroupInfoDelegate';

export interface QuoteFlowDomainParams {
  groupId: string;
  periodId: string;
  cartId: string;
  [key: string]: string;
}

export interface QuoteFlowPageConstructorParams {
  domainParams: QuoteFlowDomainParams;
  nextPageId: RouteV2Id;
}

export default class QuoteFlowDelegate {
  private _domainData: QuoteFlowDomainParams;
  private _nextPageId: RouteV2Id;
  private _groupModule: GroupModule;
  private _enrollmentPeriodModule: EnrollmentPeriodModule;
  private _cartModule: CartModule;
  private _isInitialDataLoading = false;
  public sidebarDelegate: QuoteFlowSidebarDelegate;
  public groupInfoDelegate: QuoteFlowGroupInfoDelegate;

  public get domainData(): QuoteFlowDomainParams {
    return this._domainData;
  }

  public get groupModule(): GroupModule {
    return this._groupModule;
  }

  public get enrollmentPeriodModule(): EnrollmentPeriodModule {
    return this._enrollmentPeriodModule;
  }

  public get cartModule(): CartModule {
    return this._cartModule;
  }

  /** Used for the separate layout components: sidebar, group info blob, etc. */
  public get isFlowDataLoading(): boolean {
    return this._groupModule.isLoading || this._enrollmentPeriodModule.isLoading;
  }

  /** Used for the initial page content loader */
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.isFlowDataLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFlowInitialData(): Promise<void> {
    await Promise.all([
      this._groupModule.getGroup(this._domainData.groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.periodId),
    ]);
  }

  public onCompleteRedirect(): void {
    redirectToRoute(this._nextPageId, { ...this._domainData });
  }

  constructor({ domainParams, nextPageId }: QuoteFlowPageConstructorParams) {
    this._domainData = domainParams;
    this._nextPageId = nextPageId;
    this._groupModule = new GroupModule();
    this._enrollmentPeriodModule = new EnrollmentPeriodModule();
    this._cartModule = new CartModule();
    this.sidebarDelegate = new QuoteFlowSidebarDelegate(
      this._groupModule,
      this._enrollmentPeriodModule,
      this._domainData,
    );
    this.groupInfoDelegate = new QuoteFlowGroupInfoDelegate(this._groupModule, this._enrollmentPeriodModule);

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
